import { formatPolicyValue } from "./policy";

// Human labels and value formatting for the policy registry keys shipped in D1.

const POLICY_LABELS: Readonly<Record<string, string>> = {
  "capture.scan_cooldown_seconds": "Scan cooldown",
  "capture.gps_required_on_scan": "GPS required on scan",
  "capture.allowed_methods": "Allowed methods",
  "alert.missed_checkpoint_threshold_minutes": "Missed checkpoint threshold",
  "alert.escalation_delay_minutes": "Escalation delay",
  "scheduling.site_timezone": "Site timezone",
  "scheduling.default_required_credential_types": "Default credentials",
  "timeclock.punch_grace_minutes": "Punch grace",
  "timeclock.gps_required_on_punch": "GPS required on punch",
  "branding.theme": "Theme",
  "branding.app_name": "App name",
  "privacy.location_tier": "Location tier",
  "retention.audit_event_days": "Audit retention",
};

const POLICY_HINTS: Readonly<Record<string, string>> = {
  "capture.scan_cooldown_seconds": "Seconds between repeated scans on the same device.",
  "capture.gps_required_on_scan": "Require GPS fix before a checkpoint scan submits.",
  "capture.allowed_methods": "NFC, QR, and manual capture methods enabled for this site.",
  "alert.missed_checkpoint_threshold_minutes": "Minutes after due before a missed checkpoint may alert.",
  "alert.escalation_delay_minutes": "Minutes before a missed checkpoint escalates.",
  "scheduling.site_timezone": "Local timezone for shift windows and schedule display.",
  "scheduling.default_required_credential_types": "Credential types required for new shift coverage.",
  "timeclock.punch_grace_minutes": "Grace window around scheduled punches before review.",
  "timeclock.gps_required_on_punch": "Require GPS on clock-in and clock-out punches.",
  "branding.theme": "Mobile app theme identifier for this account.",
  "branding.app_name": "Guard-facing app name on home screens.",
  "privacy.location_tier": "How aggressively location is collected across the account.",
  "retention.audit_event_days": "Days audit events are retained for this account.",
};

const LOCATION_TIER_LABELS: Readonly<Record<string, string>> = {
  shift_only: "Shift only",
  foreground_scan_only: "Scan only",
  on_duty_safety: "On-duty safety",
};

export interface PolicyEditorOption {
  value: string;
  label: string;
}

const POLICY_ENUM_OPTIONS: Readonly<Record<string, readonly PolicyEditorOption[]>> = {
  "branding.theme": [
    { value: "secops", label: "SecOps" },
    { value: "stryke", label: "Stryke" },
  ],
  "privacy.location_tier": [
    { value: "shift_only", label: LOCATION_TIER_LABELS.shift_only! },
    { value: "foreground_scan_only", label: LOCATION_TIER_LABELS.foreground_scan_only! },
    { value: "on_duty_safety", label: LOCATION_TIER_LABELS.on_duty_safety! },
  ],
};

const POLICY_STRING_ARRAY_OPTIONS: Readonly<Record<string, readonly PolicyEditorOption[]>> = {
  "capture.allowed_methods": [
    { value: "NFC", label: "NFC" },
    { value: "QR", label: "QR" },
    { value: "MANUAL", label: "Manual" },
  ],
  "scheduling.default_required_credential_types": [
    { value: "security_license", label: "Security license" },
    { value: "armed_cert", label: "Armed certification" },
    { value: "armed_license", label: "Armed license" },
    { value: "law_enforcement_officer", label: "Law enforcement officer" },
  ],
};

function optionLabel(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function includeCurrentOptions(
  options: readonly PolicyEditorOption[],
  currentValues: readonly string[],
): readonly PolicyEditorOption[] {
  const seenValues = new Set(options.map((option) => option.value));
  const currentOnly: PolicyEditorOption[] = [];
  for (const value of currentValues) {
    if (!seenValues.has(value)) {
      seenValues.add(value);
      currentOnly.push({ value, label: optionLabel(value) });
    }
  }
  return currentOnly.length === 0 ? options : [...options, ...currentOnly];
}

/** Known finite-value strings render as selects. Unknown strings remain plain text. */
export function policyEnumOptions(key: string, currentValue: unknown): readonly PolicyEditorOption[] | null {
  const options = POLICY_ENUM_OPTIONS[key];
  if (options === undefined) {
    return null;
  }
  return includeCurrentOptions(options, typeof currentValue === "string" ? [currentValue] : []);
}

/** Every shipped string-array key has a checkbox catalog; saved custom values remain selectable. */
export function policyStringArrayOptions(
  key: string,
  currentValue: unknown,
): readonly PolicyEditorOption[] {
  const options = POLICY_STRING_ARRAY_OPTIONS[key] ?? [];
  const currentValues = Array.isArray(currentValue)
    ? currentValue.filter((value): value is string => typeof value === "string")
    : [];
  return includeCurrentOptions(options, currentValues);
}

export function policyKeyLabel(key: string): string {
  if (POLICY_LABELS[key] !== undefined) {
    return POLICY_LABELS[key]!;
  }
  const dot = key.lastIndexOf(".");
  const leaf = dot === -1 ? key : key.slice(dot + 1);
  return leaf.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function policyKeyHint(key: string): string | null {
  return POLICY_HINTS[key] ?? null;
}

export function formatPolicyValueDisplay(key: string, valueType: string, value: unknown): string {
  if (valueType === "boolean" || typeof value === "boolean") {
    return value === true ? "On" : "Off";
  }
  if (key === "privacy.location_tier" && typeof value === "string") {
    return LOCATION_TIER_LABELS[value] ?? value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "None";
    }
    return value.map((entry) => String(entry)).join(", ");
  }
  if (key.endsWith("_minutes") && typeof value === "number") {
    return `${value} min`;
  }
  if (key.endsWith("_seconds") && typeof value === "number") {
    return `${value} sec`;
  }
  if (key.endsWith("_days") && typeof value === "number") {
    return `${value} days`;
  }
  return formatPolicyValue(value);
}
